import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { XCircle } from "@phosphor-icons/react";

const CHURN_REASONS = [
  "Price",
  "Product fit",
  "Switched competitor",
  "Acquisition / Merger",
  "Firm shutdown",
  "Low engagement",
  "Champion left",
  "Other",
];

/**
 * Shared "Mark as Churned" dialog — used by Admin → Firms and the Renewal
 * Pending panel on the Renewals page. Pass a `firm` to open; onConfirmed fires
 * after a successful POST so the parent can refresh its list.
 */
export default function ChurnDialog({ firm, onClose, onConfirmed }) {
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (firm) {
      setReason("");
      setDate(new Date().toISOString().slice(0, 10));
      setNotes("");
    }
  }, [firm]);

  if (!firm) return null;
  const valid = !!date;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.post(`/firms/${firm.subdomain}/churn`, {
        churn_reason: reason || null,
        churn_date: date,
        churn_notes: notes.trim() || null,
      });
      toast.success(`${firm.firm_name} marked as Churned`);
      onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Churn failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="churn-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <XCircle size={18} className="text-red-500" />
            Mark as Churned · {firm.firm_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 leading-relaxed">
            This will remove <span className="font-mono">{firm.subdomain}</span> from the main
            Dashboard, New Customers, Power Users, and CS Metrics. ETL will stop refreshing it.
            Reactivation is one click away if needed.
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-700">Reason (optional)</Label>
            <div className="mt-1.5">
              <Select value={reason || "__unset__"} onValueChange={(v) => setReason(v === "__unset__" ? "" : v)}>
                <SelectTrigger data-testid="churn-reason"><SelectValue placeholder="Pick a reason…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset__">Unspecified</SelectItem>
                  {CHURN_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-700">Churn date</Label>
            <div className="mt-1.5">
              <Input
                type="date"
                value={date}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)}
                data-testid="churn-date"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-700">
              Notes (optional) · {notes.trim().length} chars
            </Label>
            <div className="mt-1.5">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Context, who confirmed, what they said…"
                className="w-full text-sm rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                data-testid="churn-notes"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="churn-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!valid || submitting}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="churn-confirm"
          >
            {submitting ? "Marking…" : "Confirm Churn"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
